
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { QuoteBlockConfiguration } from '@/types/block-configurations';

function getQuoteChars(style: QuoteBlockConfiguration['quoteMarks']): [string, string] {
  switch (style) {
    case 'single': return ['‘', '’'];
    case 'double': return ['“', '”'];
    case 'guillemet': return ['«', '»'];
    default: return ['', ''];
  }
}

export function QuoteBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as QuoteBlockConfiguration;
  const [open, close] = getQuoteChars(config.quoteMarks);

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(block.id); }}
      onMouseEnter={(e) => { e.stopPropagation(); onHover(block.id); }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-quote cursor-pointer rounded transition-all
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
      `}
      style={{
        backgroundColor: config.background?.color || 'transparent',
        paddingTop: `${config.padding?.top ?? 20}px`,
        paddingRight: `${config.padding?.right ?? 30}px`,
        paddingBottom: `${config.padding?.bottom ?? 20}px`,
        paddingLeft: `${config.padding?.left ?? 30}px`,
        textAlign: config.alignment?.horizontal || 'left',
      }}
    >
      {/* Border + inner padding sit on the blockquote, matching QuoteBlockRenderer.cs */}
      <blockquote
        style={{
          margin: 0,
          paddingLeft: '16px',
          borderLeftWidth: `${config.border?.width ?? 4}px`,
          borderLeftStyle: config.border?.style || 'solid',
          borderLeftColor: config.border?.color || '#CCCCCC',
        }}
      >
        <p
          style={{
            fontSize: `${config.typography?.fontSize ?? 18}px`,
            fontWeight: config.typography?.fontWeight || 'normal',
            color: config.typography?.color || '#333333',
            fontFamily: config.typography?.fontFamily || 'Georgia',
            lineHeight: config.typography?.lineHeight ?? 1.6,
            fontStyle: 'italic',
            margin: 0,
          }}
        >
          {open}{config.text || 'Enter your quote text here...'}{close}
        </p>
        {(config.author || config.source) && (
          // Matches the backend attribution line: "— author, source" at 14px #666666
          <footer
            className="mt-2"
            style={{
              fontSize: '14px',
              color: '#666666',
              fontStyle: 'normal',
            }}
          >
            — {[config.author, config.source].filter(Boolean).join(', ')}
          </footer>
        )}
      </blockquote>
    </div>
  );
}
